import { CurrencyEnum, FormInput, Modal, PrimaryButton, toast } from '@systemeio/ui'
import { ErrorResponseContentInterface } from '@systemeio/util'
import { AffiliateWithdrawalTypesEnum } from 'modules/profile/affiliate-settings/enums/affiliate-withdrawal-enum'
import { useWiseRecipientApi } from 'modules/profile/affiliate-settings/hooks/use-wise-recipient-api'
import {
  AffiliateSettingsInterface,
  WiseRecipientApiInterface,
} from 'modules/profile/affiliate-settings/types/affiliate-settings-interface'
import { WiseRecipientDetailsInterface } from 'modules/profile/affiliate-settings/types/wise-details-interface'
import { AdditionalFormFields } from 'modules/profile/affiliate-settings/utils/get-wise-recipient-form'
import React, { useState } from 'react'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { KeyedMutator } from 'swr'

const defaultErrors: ErrorResponseContentInterface = {
  common: [],
  fields: {},
}
interface AddBankDetailsModalProps {
  currency: CurrencyEnum
  mutateAffiliateSettings: KeyedMutator<AffiliateSettingsInterface>
}

function AddBankDetailsModal({ currency, mutateAffiliateSettings }: AddBankDetailsModalProps) {
  const { t } = useLocoTranslation()
  const [opened, setOpened] = useState(false)
  const [tempState, setTempState] = useState<WiseRecipientApiInterface>({
    accountHolderName: '',
    currency: currency,
    recipientDetailsType: '',
    details: {},
  })
  const [formData, setFormData] = useState<WiseRecipientDetailsInterface | null>(null)
  const [errors, setErrors] = useState(defaultErrors)
  const [isFetching, setIsFetching] = useState(false)

  const { getDefaultWiseRecipientDetails, addWiseRecipientDetails } = useWiseRecipientApi(setErrors)

  async function onModalOpen() {
    if (!formData) {
      try {
        setIsFetching(true)
        const responseData = await getDefaultWiseRecipientDetails(currency)
        setFormData(responseData)
        setIsFetching(false)
      } catch (e) {
        setIsFetching(false)
      }
    }
  }

  function handleChangeWiseRecipientDetails(key: string, value: string) {
    setTempState(prev => {
      return { ...prev, details: { ...prev.details, [key]: value } }
    })
  }

  function handleChangeRecipientDetailsType(value: string) {
    if (value !== tempState.recipientDetailsType) {
      setTempState(prev => {
        return { ...prev, recipientDetailsType: value, details: {} }
      })
    }
  }

  function resetFormErrors() {
    setErrors(defaultErrors)
  }

  function updateFormDetails(details: Record<string, string>) {
    setTempState(prev => {
      return { ...prev, details: details }
    })
  }

  async function handleConfirm() {
    try {
      setIsFetching(true)
      resetFormErrors()
      const responseData = await addWiseRecipientDetails(tempState)
      await mutateAffiliateSettings(data => {
        if (data) {
          return {
            ...data,
            wiseRecipients: [...data.wiseRecipients, responseData],
            withdrawalMethod: AffiliateWithdrawalTypesEnum.bank_transfer,
          }
        }
      }, false)
      setIsFetching(false)
      setOpened(false)
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
    }
  }

  return (
    <>
      <div
        onClick={() => {
          setOpened(true)
          onModalOpen()
        }}
        className="text-brand-primary cursor-pointer"
      >
        {t('dashboard.user_settings.affiliate_commissions.wire_table.add_bank_details_title')}
      </div>
      <Modal
        isFetching={isFetching}
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('dashboard.user_settings.affiliate_commissions.wire_table.add_bank_details_title')}
      >
        <form className="flex flex-col gap-6 lg:gap-10">
          <div className="flex flex-col justify-center gap-3">
            <FormInput
              value={tempState.accountHolderName || ''}
              label={t('dashboard.user_settings.affiliate_commissions.account_holder_name_title')}
              onChange={e =>
                setTempState(prev => {
                  return { ...prev, accountHolderName: e.target.value }
                })
              }
              error={errors.fields.accountHolderName?.join('')}
              required
            />
            {formData ? (
              <AdditionalFormFields
                formData={formData}
                setFormData={setFormData}
                state={tempState}
                onChangeDetails={handleChangeWiseRecipientDetails}
                onChangeRecipientDetailsType={handleChangeRecipientDetailsType}
                fieldErrors={errors.fields}
                resetFormErrors={resetFormErrors}
                updateFormDetails={updateFormDetails}
              />
            ) : (
              <div className={'flex justify-center'}>
                <Loader />
              </div>
            )}
            {errors.common && (
              <>
                {errors.common.map((error, index) => (
                  <p className="text-red px-6 text-center text-sm" key={index}>
                    {error}
                  </p>
                ))}
              </>
            )}
          </div>
          <div className="flex justify-center gap-4">
            <PrimaryButton type="button" onClick={handleConfirm} disabled={!formData || isFetching}>
              {t('global.save')}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </>
  )
}

export default AddBankDetailsModal
